import {
  ILLUSTRATOR_MAX_PAGE_COUNT,
  ILLUSTRATOR_MAX_SOURCE_BYTES,
} from '@/convex/lib/illustrator_intake_contract';
import { COMPOSER_UPLOAD_MAX_BYTES } from '@/lib/uploads/composer-upload-route';
import { normalizeLocale, type SupportedLocale } from './config';

/**
 * What uploads accept and how they behave, shown where files are dropped,
 * in the upload tray, and on the background-uploads pill. Templates take
 * `{maxSize}`, `{aiMaxSize}` and `{aiMaxArtboards}`, filled from the upload
 * policy by formatUploadLimitsCopy.
 */
type UploadLimitsCopy = {
  /** Chat attachments: formats plus the per-file and Illustrator limits. */
  chatFormats: string;
  /** Uploads to a folder: what IMAI can read, plus the Illustrator limits. */
  folderFormats: string;
  /** Uploads run in the page (web and desktop), so a reload or close stops them. */
  keepsGoing: string;
};

const COPY: Record<SupportedLocale, UploadLimitsCopy> = {
  en: {
    chatFormats:
      'PDF, Illustrator .ai (saved PDF-compatible), images, video, Office files and ZIP, up to {maxSize} each. Illustrator files: up to {aiMaxSize} and {aiMaxArtboards} artboards.',
    folderFormats:
      'IMAI reads PDF, Illustrator .ai (saved PDF-compatible, up to {aiMaxSize} and {aiMaxArtboards} artboards), images, video, Office files and ZIP.',
    keepsGoing:
      "Uploads keep going while you move around IMAI. Don't reload or close this page.",
  },
  ar: {
    chatFormats:
      'PDF وملفات Illustrator ‎.ai (محفوظة متوافقة مع PDF) والصور والفيديو وملفات Office وZIP، حتى {maxSize} لكل ملف. ملفات Illustrator: حتى {aiMaxSize} و{aiMaxArtboards} لوح رسم.',
    folderFormats:
      'يقرأ IMAI ملفات PDF وIllustrator ‎.ai (محفوظة متوافقة مع PDF، حتى {aiMaxSize} و{aiMaxArtboards} لوح رسم) والصور والفيديو وملفات Office وZIP.',
    keepsGoing:
      'يستمر الرفع أثناء تنقلك في IMAI. لا تُعِد تحميل هذه الصفحة ولا تغلقها.',
  },
  es: {
    chatFormats:
      'PDF, Illustrator .ai (guardado compatible con PDF), imágenes, vídeo, archivos de Office y ZIP, hasta {maxSize} cada uno. Archivos de Illustrator: hasta {aiMaxSize} y {aiMaxArtboards} mesas de trabajo.',
    folderFormats:
      'IMAI lee PDF, Illustrator .ai (guardado compatible con PDF, hasta {aiMaxSize} y {aiMaxArtboards} mesas de trabajo), imágenes, vídeo, archivos de Office y ZIP.',
    keepsGoing:
      'Las subidas continúan mientras navegas por IMAI. No recargues ni cierres esta página.',
  },
  'pt-BR': {
    chatFormats:
      'PDF, Illustrator .ai (salvo compatível com PDF), imagens, vídeo, arquivos do Office e ZIP, até {maxSize} cada. Arquivos do Illustrator: até {aiMaxSize} e {aiMaxArtboards} pranchetas.',
    folderFormats:
      'O IMAI lê PDF, Illustrator .ai (salvo compatível com PDF, até {aiMaxSize} e {aiMaxArtboards} pranchetas), imagens, vídeo, arquivos do Office e ZIP.',
    keepsGoing:
      'Os envios continuam enquanto você navega pelo IMAI. Não recarregue nem feche esta página.',
  },
  fr: {
    chatFormats:
      "PDF, Illustrator .ai (enregistré compatible PDF), images, vidéo, fichiers Office et ZIP, jusqu'à {maxSize} chacun. Fichiers Illustrator : jusqu'à {aiMaxSize} et {aiMaxArtboards} plans de travail.",
    folderFormats:
      "IMAI lit les PDF, les fichiers Illustrator .ai (enregistrés compatibles PDF, jusqu'à {aiMaxSize} et {aiMaxArtboards} plans de travail), les images, la vidéo, les fichiers Office et les ZIP.",
    keepsGoing:
      "Les envois continuent pendant que vous naviguez dans IMAI. N'actualisez pas et ne fermez pas cette page.",
  },
  it: {
    chatFormats:
      'PDF, Illustrator .ai (salvato compatibile PDF), immagini, video, file Office e ZIP, fino a {maxSize} ciascuno. File Illustrator: fino a {aiMaxSize} e {aiMaxArtboards} tavole da disegno.',
    folderFormats:
      'IMAI legge PDF, Illustrator .ai (salvato compatibile PDF, fino a {aiMaxSize} e {aiMaxArtboards} tavole da disegno), immagini, video, file Office e ZIP.',
    keepsGoing:
      'I caricamenti proseguono mentre ti sposti in IMAI. Non ricaricare né chiudere questa pagina.',
  },
  ko: {
    chatFormats:
      'PDF, Illustrator .ai(PDF 호환으로 저장), 이미지, 동영상, Office 파일, ZIP을 파일당 최대 {maxSize}까지 지원합니다. Illustrator 파일: 최대 {aiMaxSize}, 대지 {aiMaxArtboards}개.',
    folderFormats:
      'IMAI는 PDF, Illustrator .ai(PDF 호환으로 저장, 최대 {aiMaxSize}, 대지 {aiMaxArtboards}개), 이미지, 동영상, Office 파일, ZIP을 읽을 수 있습니다.',
    keepsGoing:
      'IMAI에서 다른 곳으로 이동해도 업로드는 계속됩니다. 이 페이지를 새로고침하거나 닫지 마세요.',
  },
  de: {
    chatFormats:
      'PDF, Illustrator .ai (PDF-kompatibel gespeichert), Bilder, Video, Office-Dateien und ZIP, je bis zu {maxSize}. Illustrator-Dateien: bis zu {aiMaxSize} und {aiMaxArtboards} Zeichenflächen.',
    folderFormats:
      'IMAI liest PDF, Illustrator .ai (PDF-kompatibel gespeichert, bis zu {aiMaxSize} und {aiMaxArtboards} Zeichenflächen), Bilder, Video, Office-Dateien und ZIP.',
    keepsGoing:
      'Uploads laufen weiter, während du dich in IMAI bewegst. Lade diese Seite nicht neu und schließe sie nicht.',
  },
  ja: {
    chatFormats:
      'PDF、Illustrator .ai（PDF互換で保存）、画像、動画、Officeファイル、ZIPに対応。1ファイル最大{maxSize}。Illustratorファイルは最大{aiMaxSize}、アートボード{aiMaxArtboards}枚まで。',
    folderFormats:
      'IMAIはPDF、Illustrator .ai（PDF互換で保存、最大{aiMaxSize}・アートボード{aiMaxArtboards}枚）、画像、動画、Officeファイル、ZIPを読み込めます。',
    keepsGoing:
      'IMAI内で移動してもアップロードは続きます。このページを再読み込みしたり閉じたりしないでください。',
  },
  hi: {
    chatFormats:
      'PDF, Illustrator .ai (PDF-संगत रूप में सेव), इमेज, वीडियो, Office फ़ाइलें और ZIP, हर फ़ाइल {maxSize} तक। Illustrator फ़ाइलें: {aiMaxSize} और {aiMaxArtboards} आर्टबोर्ड तक।',
    folderFormats:
      'IMAI PDF, Illustrator .ai (PDF-संगत रूप में सेव, {aiMaxSize} और {aiMaxArtboards} आर्टबोर्ड तक), इमेज, वीडियो, Office फ़ाइलें और ZIP पढ़ सकता है।',
    keepsGoing:
      'IMAI में कहीं भी जाने पर अपलोड चलते रहते हैं। यह पेज रीलोड या बंद न करें।',
  },
  ru: {
    chatFormats:
      'PDF, Illustrator .ai (сохранённые с совместимостью с PDF), изображения, видео, файлы Office и ZIP, до {maxSize} каждый. Файлы Illustrator: до {aiMaxSize} и {aiMaxArtboards} монтажных областей.',
    folderFormats:
      'IMAI читает PDF, Illustrator .ai (сохранённые с совместимостью с PDF, до {aiMaxSize} и {aiMaxArtboards} монтажных областей), изображения, видео, файлы Office и ZIP.',
    keepsGoing:
      'Загрузка продолжается, пока вы переходите по IMAI. Не перезагружайте и не закрывайте эту страницу.',
  },
  zh: {
    chatFormats:
      '支持 PDF、Illustrator .ai（以 PDF 兼容方式保存）、图片、视频、Office 文件和 ZIP，每个文件最大 {maxSize}。Illustrator 文件：最大 {aiMaxSize}，最多 {aiMaxArtboards} 个画板。',
    folderFormats:
      'IMAI 可读取 PDF、Illustrator .ai（以 PDF 兼容方式保存，最大 {aiMaxSize}，最多 {aiMaxArtboards} 个画板）、图片、视频、Office 文件和 ZIP。',
    keepsGoing: '在 IMAI 中切换页面时上传会继续。请不要刷新或关闭此页面。',
  },
  'zh-TW': {
    chatFormats:
      '支援 PDF、Illustrator .ai（以 PDF 相容方式儲存）、圖片、影片、Office 檔案和 ZIP，每個檔案最大 {maxSize}。Illustrator 檔案：最大 {aiMaxSize}，最多 {aiMaxArtboards} 個工作區域。',
    folderFormats:
      'IMAI 可讀取 PDF、Illustrator .ai（以 PDF 相容方式儲存，最大 {aiMaxSize}，最多 {aiMaxArtboards} 個工作區域）、圖片、影片、Office 檔案和 ZIP。',
    keepsGoing: '在 IMAI 中切換頁面時上傳會繼續。請不要重新整理或關閉此頁面。',
  },
  vi: {
    chatFormats:
      'PDF, Illustrator .ai (lưu tương thích PDF), hình ảnh, video, tệp Office và ZIP, tối đa {maxSize} mỗi tệp. Tệp Illustrator: tối đa {aiMaxSize} và {aiMaxArtboards} artboard.',
    folderFormats:
      'IMAI đọc được PDF, Illustrator .ai (lưu tương thích PDF, tối đa {aiMaxSize} và {aiMaxArtboards} artboard), hình ảnh, video, tệp Office và ZIP.',
    keepsGoing:
      'Tệp vẫn tiếp tục tải lên khi bạn chuyển trang trong IMAI. Đừng tải lại hoặc đóng trang này.',
  },
  id: {
    chatFormats:
      'PDF, Illustrator .ai (disimpan kompatibel PDF), gambar, video, file Office, dan ZIP, hingga {maxSize} per file. File Illustrator: hingga {aiMaxSize} dan {aiMaxArtboards} artboard.',
    folderFormats:
      'IMAI dapat membaca PDF, Illustrator .ai (disimpan kompatibel PDF, hingga {aiMaxSize} dan {aiMaxArtboards} artboard), gambar, video, file Office, dan ZIP.',
    keepsGoing:
      'Unggahan tetap berjalan saat Anda berpindah halaman di IMAI. Jangan muat ulang atau tutup halaman ini.',
  },
};

const GIB = 1024 * 1024 * 1024;
const MIB = 1024 * 1024;

/** A whole-number size in the locale's short unit: "2 GB", "512 MB". */
export function formatUploadLimitSize(bytes: number, locale: string): string {
  const [value, unit] =
    bytes >= GIB ? [bytes / GIB, 'gigabyte'] : [bytes / MIB, 'megabyte'];
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits: 0,
  }).format(value);
}

/** Every upload-limits line for the locale, with the policy's numbers filled in. */
export function getUploadLimitsCopy(locale?: string | null): UploadLimitsCopy {
  const normalized = normalizeLocale(locale);
  const values: Record<string, string> = {
    maxSize: formatUploadLimitSize(COMPOSER_UPLOAD_MAX_BYTES, normalized),
    aiMaxSize: formatUploadLimitSize(ILLUSTRATOR_MAX_SOURCE_BYTES, normalized),
    aiMaxArtboards: new Intl.NumberFormat(normalized).format(
      ILLUSTRATOR_MAX_PAGE_COUNT
    ),
  };
  const fill = (template: string) =>
    template.replace(
      /\{(\w+)\}/g,
      (match, key: string) => values[key] ?? match
    );
  const copy = COPY[normalized];
  return {
    chatFormats: fill(copy.chatFormats),
    folderFormats: fill(copy.folderFormats),
    keepsGoing: copy.keepsGoing,
  };
}
